import React from "react";
import * as Sentry from "@sentry/react";
import { bool, node, shape } from "prop-types";

import { withSentryErrorBoundary } from "src/shared/components/SentryErrorBoundary";
import { formatMoney } from "src/shared/utilities/formatMoney";
import buildingProps from "src/shared/utilities/graphql/buildingProps";
import listingProps from "src/shared/utilities/graphql/listingProps";

import {
  formatAddressForDesktop,
  formatAddressForMobile,
  formatPath
} from "../../experts/utilities/listing";
import * as S from "./styled";

const renderMobileSticky = (children, { building, listing }) => (
  <S.StickyMobileContainer>
    <S.StickyButtonSection>{children}</S.StickyButtonSection>
    <S.ListingInformation>
      {listing ? (
        <>
          <S.SmallListingText>
            {formatAddressForMobile(listing.title_with_unit)}
          </S.SmallListingText>
          <S.Dot />
          <S.SmallListingText bold>
            {formatMoney(listing.listed_price)}
          </S.SmallListingText>
        </>
      ) : (
        <S.SmallListingText>
          {formatAddressForMobile(building.address.pretty_address)}
        </S.SmallListingText>
      )}
    </S.ListingInformation>
  </S.StickyMobileContainer>
);

const renderDesktopSticky = (children, { building, listing }) => (
  <S.StickyDesktopContainer data-qa="sticky-desktop-contact-container">
    <S.ListingInformation>
      {listing ? (
        <>
          <S.BuildingLink href={formatPath(listing.building.url)}>
            {formatAddressForDesktop(listing.title_with_unit)}
          </S.BuildingLink>
          <S.Dot />
          <S.MediumListingText>
            {formatMoney(listing.listed_price)}
          </S.MediumListingText>
          <S.RoomInformation>
            <S.SmallListingText border>
              {listing.anyrooms_description}
            </S.SmallListingText>
            <S.SmallListingText border>
              {listing.bedrooms_description}
            </S.SmallListingText>
            <S.SmallListingText>{listing.baths_description}</S.SmallListingText>
          </S.RoomInformation>
        </>
      ) : (
        <S.MediumListingText>
          {formatAddressForDesktop(building.address.pretty_address)}
        </S.MediumListingText>
      )}
    </S.ListingInformation>
    <S.StickyButtonSection>{children}</S.StickyButtonSection>
  </S.StickyDesktopContainer>
);

const StickyContactContainer = ({ building, children, isMobile, listing }) => {
  if (!listing && !building) {
    Sentry.captureMessage(
      "Cannot load sticky header or footer without a listing or building defined"
    );
    return null;
  }

  return isMobile
    ? renderMobileSticky(children, { building, listing })
    : renderDesktopSticky(children, { building, listing });
};

StickyContactContainer.propTypes = {
  building: shape(buildingProps),
  children: node.isRequired,
  isMobile: bool.isRequired,
  listing: shape(listingProps)
};

StickyContactContainer.defaultProps = {
  building: null,
  listing: null
};

export default withSentryErrorBoundary(StickyContactContainer, {
  tags: { contact_box: "sticky-elements" }
});
